import React, { useState, useEffect } from 'react';
import { getWiki, listWikiPages, createWikiPage, getWikiPage, updateWikiPage, deleteWikiPage } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import styles from './WikiTab.module.scss';

function timeAgo(dateStr) {
    const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (d === 0) return 'сегодня';
    if (d === 1) return 'вчера';
    if (d < 30) return `${d}д. назад`;
    return new Date(dateStr).toLocaleDateString('ru-RU');
}

export default function WikiTab({ repo, isOwner }) {
    const { user } = useAuth();
    const [wiki, setWiki] = useState(null);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSlug, setSelectedSlug] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const [editing, setEditing] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ title: '', content: '' });

    useEffect(() => { loadWiki(); }, [repo.id]);

    async function loadWiki() {
        setLoading(true);
        try {
            const wikiData = await getWiki(repo.id);
            setWiki(wikiData);
            if (wikiData.is_enabled) {
                const pagesData = await listWikiPages(repo.id);
                const pList = pagesData.results || pagesData;
                setPages(pList);
                if (pList.length > 0 && !selectedSlug) setSelectedSlug(pList[0].slug);
            }
        } catch { setWiki(null); }
        setLoading(false);
    }

    useEffect(() => {
        if (!selectedSlug) { setCurrentPage(null); return; }
        getWikiPage(repo.id, selectedSlug).then(setCurrentPage).catch(() => setCurrentPage(null));
    }, [selectedSlug, repo.id]);

    async function handleCreate(e) {
        e.preventDefault();
        try {
            const page = await createWikiPage(repo.id, form);
            setCreating(false);
            setForm({ title: '', content: '' });
            const newPages = [...pages, page];
            setPages(newPages);
            setSelectedSlug(page.slug);
        } catch (err) { alert(err.message); }
    }

    async function handleSave() {
        try {
            const updated = await updateWikiPage(repo.id, selectedSlug, form);
            setCurrentPage(updated);
            setEditing(false);
            setPages(prev => prev.map(p => p.slug === selectedSlug ? { ...p, title: updated.title } : p));
        } catch (err) { alert(err.message); }
    }

    async function handleDelete() {
        if (!window.confirm('Удалить эту страницу?')) return;
        try {
            await deleteWikiPage(repo.id, selectedSlug);
            const newPages = pages.filter(p => p.slug !== selectedSlug);
            setPages(newPages);
            setSelectedSlug(newPages.length > 0 ? newPages[0].slug : null);
        } catch (err) { alert(err.message); }
    }

    function startEdit() {
        setForm({ title: currentPage.title, content: currentPage.content });
        setEditing(true);
    }

    if (loading) return <div className={styles.loading}>Загрузка...</div>;

    if (!wiki?.is_enabled) {
        return (
            <div className={styles.disabled}>
                <span style={{ fontSize: 48 }}>📖</span>
                <h3>Wiki отключена</h3>
                <p>Wiki недоступна для этого репозитория</p>
            </div>
        );
    }

    return (
        <div className={styles.wrap}>
            {/* Sidebar */}
            <div className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <span className={styles.sidebarTitle}>Страницы</span>
                    {user && (
                        <button className={styles.addBtn} onClick={() => { setCreating(true); setEditing(false); }} title="Новая страница">+</button>
                    )}
                </div>
                <div className={styles.pageList}>
                    {pages.map(p => (
                        <button
                            key={p.slug}
                            className={`${styles.pageItem} ${selectedSlug === p.slug ? styles.activePageItem : ''}`}
                            onClick={() => { setSelectedSlug(p.slug); setEditing(false); setCreating(false); }}
                        >
                            📄 {p.title}
                        </button>
                    ))}
                    {pages.length === 0 && (
                        <span className={styles.noPages}>Нет страниц</span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className={styles.content}>
                {creating ? (
                    <form className={styles.editForm} onSubmit={handleCreate}>
                        <h3 className={styles.editTitle}>Новая страница</h3>
                        <input
                            className={styles.inputField}
                            placeholder="Заголовок страницы *"
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                            required
                        />
                        <textarea
                            className={styles.editor}
                            placeholder="Содержимое страницы (Markdown)..."
                            value={form.content}
                            onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                            rows={18}
                        />
                        <div className={styles.editActions}>
                            <button type="button" className={styles.cancelBtn} onClick={() => setCreating(false)}>Отмена</button>
                            <button type="submit" className={styles.saveBtn}>Создать</button>
                        </div>
                    </form>
                ) : editing && currentPage ? (
                    <div className={styles.editForm}>
                        <h3 className={styles.editTitle}>Редактировать: {currentPage.title}</h3>
                        <input
                            className={styles.inputField}
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                        />
                        <textarea
                            className={styles.editor}
                            value={form.content}
                            onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                            rows={18}
                        />
                        <div className={styles.editActions}>
                            <button className={styles.cancelBtn} onClick={() => setEditing(false)}>Отмена</button>
                            {isOwner && <button className={styles.deleteBtn} onClick={handleDelete}>Удалить</button>}
                            <button className={styles.saveBtn} onClick={handleSave}>Сохранить</button>
                        </div>
                    </div>
                ) : currentPage ? (
                    <div className={styles.pageView}>
                        <div className={styles.pageViewHeader}>
                            <h2 className={styles.pageViewTitle}>{currentPage.title}</h2>
                            <div className={styles.pageMeta}>
                                <span>Последнее изменение: {timeAgo(currentPage.updated_at)}</span>
                                <span>Автор: {currentPage.author?.username}</span>
                            </div>
                            {user && (
                                <button className={styles.editBtn} onClick={startEdit}>✏️ Редактировать</button>
                            )}
                        </div>
                        <div className={styles.pageBody}>
                            <pre className={styles.pageContent}>{currentPage.content}</pre>
                        </div>
                    </div>
                ) : (
                    <div className={styles.emptyContent}>
                        <span style={{ fontSize: 48 }}>📖</span>
                        <p>Выберите страницу или создайте новую</p>
                    </div>
                )}
            </div>
        </div>
    );
}
